import React from 'react';
import {
  RotateCw,
  CheckCircle,
  UserPlus,
  RefreshCcw,
  MinusCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { RolloutLastInitialSync } from '../../../types/organization.types';

interface RolloutSyncStatusBannerProps {
  syncStatus: RolloutLastInitialSync;
}

/**
 * Banner component that displays the sync progress and results for Rollout FUB integration.
 * Shows during processing and displays final results when completed.
 */
export const RolloutSyncStatusBanner: React.FC<RolloutSyncStatusBannerProps> = ({ syncStatus }) => {
  const { status, progress } = syncStatus;

  // Only show banner when processing
  if (status !== 'processing') {
    return null;
  }

  const progressPercentage = progress?.percentage_complete ?? 0;
  const contactsProcessed = progress?.contacts_processed ?? 0;
  const totalContacts = progress?.total_contacts_in_crm ?? 0;

  return (
    <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-3 mb-3">
        <RotateCw className="w-5 h-5 text-orange-600 animate-spin" />
        <span className="text-sm font-semibold text-orange-900">Syncing Contacts...</span>
        <span className="text-sm text-orange-700 ml-auto">{progressPercentage.toFixed(1)}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-orange-100 rounded-full h-2 mb-3">
        <div
          className="bg-orange-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(progressPercentage, 100)}%` }}
        />
      </div>

      {/* Progress stats */}
      <div className="text-sm text-gray-600">
        <span className="font-medium text-gray-900">{contactsProcessed.toLocaleString()}</span>{' '}
        contacts processed of{' '}
        <span className="font-medium text-gray-900">{totalContacts.toLocaleString()}</span> total
      </div>
    </div>
  );
};

interface RolloutSyncResultsProps {
  syncStatus: RolloutLastInitialSync;
}

/**
 * Component that displays the sync results after completion.
 * Shows created, updated, skipped counts and skip reasons.
 */
export const RolloutSyncResults: React.FC<RolloutSyncResultsProps> = ({ syncStatus }) => {
  const {
    status,
    total_created,
    total_updated,
    total_skipped,
    total_failed,
    errors_sample,
    skip_reasons,
  } = syncStatus;

  // Only show results when completed or failed
  if (status === 'processing') {
    return null;
  }

  const isFailed = status === 'failed';

  return (
    <div
      className={`bg-gradient-to-r ${isFailed ? 'from-red-50 to-rose-50 border-red-200' : 'from-green-50 to-emerald-50 border-green-200'} border rounded-lg p-4 mb-4`}
    >
      <div className="flex items-center gap-3 mb-3">
        {isFailed ? (
          <XCircle className="w-5 h-5 text-red-600" />
        ) : (
          <CheckCircle className="w-5 h-5 text-green-600" />
        )}
        <span className={`text-sm font-semibold ${isFailed ? 'text-red-900' : 'text-green-900'}`}>
          {isFailed ? 'Sync Failed' : 'Sync Complete'}
        </span>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
        <div className="flex items-center gap-1.5">
          <UserPlus className="w-4 h-4 text-green-600" />
          <span className="font-medium text-gray-900">{total_created.toLocaleString()}</span>
          <span>created</span>
        </div>
        <div className="flex items-center gap-1.5">
          <RefreshCcw className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-gray-900">{total_updated.toLocaleString()}</span>
          <span>updated</span>
        </div>
        <div className="flex items-center gap-1.5">
          <MinusCircle className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-900">{total_skipped.toLocaleString()}</span>
          <span>skipped</span>
        </div>
        {isFailed && total_failed > 0 && (
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="font-medium text-gray-900">{total_failed.toLocaleString()}</span>
            <span>failed</span>
          </div>
        )}
      </div>

      {/* Skip reasons and errors - responsive two-column layout */}
      {(skip_reasons &&
        (skip_reasons.missing_speculo_sync_tag || skip_reasons.missing_email_and_phone)) ||
      (isFailed && errors_sample && errors_sample.length > 0) ? (
        <div
          className={`mt-3 pt-3 border-t ${isFailed ? 'border-red-200' : 'border-green-200'} text-sm text-gray-600`}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Skip reasons - left side */}
            {skip_reasons && Object.keys(skip_reasons).length > 0 && (
              <div className="flex-1">
                <p className="font-medium text-gray-800 mb-1">Skip Reasons:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {Object.entries(skip_reasons).map(
                    ([key, value]) =>
                      value !== undefined &&
                      value > 0 && (
                        <li key={key} className="text-xs text-gray-700">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}:{' '}
                          <span className="font-medium">{value.toLocaleString()}</span>
                        </li>
                      )
                  )}
                </ul>
              </div>
            )}

            {/* Error samples - right side, only show when failed */}
            {isFailed && errors_sample && errors_sample.length > 0 && (
              <div className="flex-1">
                <p className="font-medium text-red-800 mb-1">Errors:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {errors_sample.map((error, index) => (
                    <li key={index} className="text-xs text-red-700">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
